import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import MoreHorizOutlined from "@mui/icons-material/MoreHorizOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import moment from "moment";
import useStyles from "./style";
import { mapContractRecord } from "../mapContract";
import ContractsApi from "Services/Contracts";
import SettingsApi from "Services/Settings";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { MOCK_TEMPLATES } from "../mockTemplates";

const TABS = ["Overview", "Key clauses", "Parties", "Timeline", "Documents", "Obligations", "Amendments"];

const TEMPLATE_ACCENT_HEX = {
  EMPLOYMENT: "#0f7b3a",
  COMMERCIAL: "#2563EB",
  CONFIDENTIALITY: "#F9830D",
};
const DEFAULT_ACCENT_HEX = "#0f7b3a";

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "--");
const formatValue = (value, currency) =>
  typeof value === "number" ? `${value.toLocaleString("en-US")} ${currency || ""}`.trim() : "--";

const PanelHeader = ({ classes, children }) => (
  <Box className={classes.panelHeader}>
    <Typography className={classes.panelHeaderText}>{children}</Typography>
  </Box>
);

const isEmptyValue = (value) => value === null || value === undefined || value === "";

const FieldPair = ({ classes, label, value, format }) => {
  const empty = isEmptyValue(value);

  return (
    <Box>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <Typography className={empty ? classes.fieldValueMuted : classes.fieldValue}>
        {empty ? "--" : format ? format(value) : value}
      </Typography>
    </Box>
  );
};

const ContractDetail = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchContract = useCallback(async () => {
    dispatch(showLoader("Loading please wait..."));
    setLoading(true);

    try {
      const response = await ContractsApi.getContractById(id);
      const record = response?.data?.data ?? null;
      setContract(record ? mapContractRecord(record) : null);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Failed to fetch contract",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setContract(null);
    } finally {
      dispatch(hideLoader());
      setLoading(false);
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const handleDownloadContract = useCallback(async () => {
    if (!contract?.owner || downloading) {
      return;
    }

    setDownloading(true);

    try {
      const response = await SettingsApi.downloadDriverContract(contract.owner);
      const blob = new Blob([response?.data], {
        type: response?.headers?.["content-type"] || "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Contract-${contract.name || contract.owner}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Failed to download contract",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    } finally {
      setDownloading(false);
    }
  }, [contract, dispatch, downloading]);

  const daysRemaining = contract?.endDate
    ? moment(contract.endDate).diff(moment(), "days")
    : null;

  if (!contract) {
    return (
      <Box className={classes.page}>
        <Box className={classes.headerBar}>
          <Box className={classes.headerLeft}>
            <IconButton aria-label="back" onClick={() => navigate("/contracts")}>
              <ArrowBackRoundedIcon />
            </IconButton>
            <Typography className={classes.headerTitle}>
              {loading ? "Loading contract..." : "Contract not found"}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.headerBar}>
        <Box className={classes.headerLeft}>
          <IconButton aria-label="back" onClick={() => navigate("/contracts")}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <Box className={classes.headerIconBadge}>
            <DescriptionOutlined />
          </Box>
          <Box className={classes.headerTitleGroup}>
            <Typography className={classes.headerTitle} title={contract.name}>
              {contract.name}
            </Typography>
            <Typography className={classes.headerReference}>{contract.reference}</Typography>
          </Box>
        </Box>

        <Box className={classes.headerActions}>
          <Chip
            label={contract.status}
            size="small"
            className={`${classes.statusChip} ${
              contract.status === "approved" ? classes.statusApproved : classes.statusDraft
            }`}
          />
          <Button variant="outlined">Share</Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleDownloadContract}
            disabled={!contract.owner || downloading}
          >
            {downloading ? "Téléchargement..." : "Télécharger"}
          </Button>
          <IconButton aria-label="more options">
            <MoreHorizOutlined />
          </IconButton>
          <Button variant="outlined" className={classes.deleteButton}>
            Delete
          </Button>
          <Button variant="contained" color="primary">
            Edit contract
          </Button>
        </Box>
      </Box>

      <Tabs
        className={classes.tabsBar}
        value={tabIndex}
        onChange={(event, newValue) => setTabIndex(newValue)}
      >
        {TABS.map((tab) => (
          <Tab key={tab} label={tab} />
        ))}
      </Tabs>

      {tabIndex === 0 && (
        <Box className={classes.tabContent}>
          <Box className={classes.statsRow}>
            <Box className={classes.statCard}>
              <Typography className={classes.statLabel}>Contract value</Typography>
              <Typography className={classes.statValue}>
                {formatValue(contract.value, contract.valueCurrency)}
              </Typography>
            </Box>
            <Box className={classes.statCard}>
              <Typography className={classes.statLabel}>Days remaining</Typography>
              <Typography className={classes.statValue}>
                {daysRemaining === null ? "--" : daysRemaining}
              </Typography>
              <Typography className={classes.statSubtext}>
                {contract.endDate ? `Ends ${formatDate(contract.endDate)}` : "No end date"}
              </Typography>
            </Box>
            <Box className={classes.statCard}>
              <Typography className={classes.statLabel}>Risk score</Typography>
              <Typography className={classes.statValue}>
                {contract.riskScore === null || contract.riskScore === undefined
                  ? "Not scored"
                  : contract.riskScore}
              </Typography>
            </Box>
            <Box className={classes.statCard}>
              <Typography className={classes.statLabel}>Clauses</Typography>
              <Typography className={classes.statValue}>{contract.clauses}</Typography>
              <Typography className={classes.statSubtext}>
                {contract.approvals} approvals - {contract.signatures} signatures
              </Typography>
            </Box>
          </Box>

          <Box className={classes.contentGrid}>
            <Box className={classes.panel}>
              <PanelHeader classes={classes}>Contract details</PanelHeader>
              <Box className={classes.panelBody}>
                <Box className={classes.detailGrid}>
                  <FieldPair classes={classes} label="Type" value={contract.type} />
                  <FieldPair classes={classes} label="Reference" value={contract.reference} />

                  <FieldPair classes={classes} label="Counterparty" value={contract.counterparty} />
                  <FieldPair
                    classes={classes}
                    label="Counterparty email"
                    value={contract.counterpartyEmail}
                  />

                  <FieldPair classes={classes} label="Owner" value={contract.owner} />
                  <FieldPair classes={classes} label="Template" value={contract.template} />

                  <FieldPair
                    classes={classes}
                    label="Start date"
                    value={contract.startDate}
                    format={formatDate}
                  />
                  <FieldPair
                    classes={classes}
                    label="End date"
                    value={contract.endDate}
                    format={formatDate}
                  />

                  <FieldPair classes={classes} label="Governing law" value={contract.governingLaw} />
                  <FieldPair classes={classes} label="Notice period" value={contract.noticePeriod} />

                  <FieldPair classes={classes} label="Auto-renew" value={contract.autoRenew ? "Yes" : "No"} />
                  <FieldPair classes={classes} label="Payment terms" value={contract.paymentTerms} />
                </Box>
              </Box>
            </Box>

            <Box className={classes.sidePanelStack}>
              <Box className={classes.panel}>
                <PanelHeader classes={classes}>Risk assessment</PanelHeader>
                <Box className={classes.panelBody}>
                  <Box className={classes.riskRow}>
                    <Typography className={classes.fieldLabel}>Overall risk</Typography>
                    <Chip
                      label={
                        contract.riskScore === null || contract.riskScore === undefined
                          ? "Risk not scored"
                          : `Score ${contract.riskScore}`
                      }
                      size="small"
                      className={classes.riskChip}
                    />
                  </Box>
                  <Box className={classes.riskBar}>
                    <Box
                      className={classes.riskBarFill}
                      style={{ width: `${contract.riskScore || 0}%` }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box className={classes.panel}>
                <PanelHeader classes={classes}>Contract snapshot</PanelHeader>
                <Box className={classes.panelBody}>
                  <Box className={classes.snapshotGrid}>
                    <FieldPair classes={classes} label="Template" value={contract.template} />
                    <FieldPair classes={classes} label="Department" value={contract.department} />

                    <FieldPair
                      classes={classes}
                      label="Counterparty email"
                      value={contract.counterpartyEmail}
                    />
                    <FieldPair classes={classes} label="Clauses" value={contract.clauses} />

                    <FieldPair classes={classes} label="Approvals" value={contract.approvals} />
                    <FieldPair classes={classes} label="Signatures" value={contract.signatures} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {tabIndex === 2 && (
        <Box className={classes.tabContent}>
          <Box className={classes.contentGrid}>
            <Box className={classes.panel}>
              <PanelHeader classes={classes}>Parties &amp; ownership</PanelHeader>
              <Box className={classes.panelBody}>
                <Box className={classes.detailGrid}>
                  <FieldPair classes={classes} label="Counterparty" value={contract.counterparty} />
                  <FieldPair
                    classes={classes}
                    label="Counterparty email"
                    value={contract.counterpartyEmail}
                  />

                  <FieldPair classes={classes} label="Owner" value={contract.owner} />
                  <FieldPair classes={classes} label="Department" value={contract.department} />

                  <FieldPair classes={classes} label="Template" value={contract.template} />
                  <FieldPair classes={classes} label="Governing law" value={contract.governingLaw} />
                </Box>
              </Box>
            </Box>

            <Box className={classes.panel}>
              <Box className={classes.panelHeader}>
                <Box className={classes.panelHeaderRow}>
                  <Typography className={classes.panelHeaderText}>Signatures</Typography>
                  <Box className={classes.countBadge}>{contract.signatures}</Box>
                </Box>
              </Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Signer</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} className={classes.signaturesEmpty}>
                      No signatures available for this contract.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}

      {tabIndex === 4 && (
        <Box className={classes.tabContent}>
          <Box className={classes.templatesHeader}>
            <Typography className={classes.templatesTitle}>All Templates</Typography>
            <Typography className={classes.templatesCount}>{MOCK_TEMPLATES.length}</Typography>
          </Box>

          <Box className={classes.templatesGrid}>
            {MOCK_TEMPLATES.map((template) => {
              const accentColor = TEMPLATE_ACCENT_HEX[template.category] || DEFAULT_ACCENT_HEX;

              return (
                <Box key={template.id} className={classes.templateCard}>
                  <Box
                    className={classes.templateThumb}
                    style={{ backgroundColor: `${accentColor}14` }}
                  >
                    <Box className={classes.templateThumbRibbon} style={{ color: accentColor }} />
                    <ArticleOutlined style={{ color: accentColor, fontSize: 30 }} />
                    <Box
                      className={classes.templateThumbLineAccent}
                      style={{ backgroundColor: accentColor }}
                    />
                    <Box className={classes.templateThumbLine} />
                  </Box>

                  <Box className={classes.templateBody}>
                    <Typography className={classes.templateCategory}>{template.category}</Typography>
                    <Typography className={classes.templateTitle}>{template.title}</Typography>
                    <Typography className={classes.templateDescription}>
                      {template.description}
                    </Typography>

                    <Box className={classes.templateFooterRow}>
                      <Box className={classes.templateMetaGroup}>
                        <Box className={classes.templateMetaItem}>
                          <DescriptionOutlined />
                          {template.clauses}
                        </Box>
                        <Box className={classes.templateMetaItem}>
                          <AccessTimeOutlined />
                          {template.updatedDaysAgo}d ago
                        </Box>
                      </Box>
                      <Box className={classes.templateStatus}>
                        <Box className={classes.templateStatusDot} />
                        {template.status}
                      </Box>
                    </Box>

                    <Typography className={classes.templateUsage}>
                      Used {template.usedCount}x &middot; {template.adoption}% adoption
                    </Typography>
                    <Box className={classes.templateProgressTrack}>
                      <Box
                        className={classes.templateProgressFill}
                        style={{ width: `${template.adoption}%` }}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {![0, 2, 4].includes(tabIndex) && (
        <Box className={classes.placeholderTab}>
          <Typography>No data available yet for {TABS[tabIndex]}.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ContractDetail;
